import type {
  EventCalendarCategory,
  EventCalendarColor,
  EventCalendarEvent,
  EventCalendarView,
} from '@all-manager/design-system-v2';
import {
  addMinutes,
  addMonths,
  endOfMonth,
  endOfWeek,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { CalendarItemDatabase } from '../../../types';
import { isMidnight } from '../../../utils/formatValues';
import { CalendarItemType, CalendarRange } from '../types/calendar';

// Meetings and reminders only store a start: they are shown as one-hour slots.
const DEFAULT_DURATION_MINUTES = 60;

const WEEK_OPTIONS = { weekStartsOn: 1 } as const;

export const CALENDAR_CATEGORIES: EventCalendarCategory[] = [
  { id: 'event', label: 'Rendez-vous', color: 'teal' },
  { id: 'council_meeting', label: 'Réunions du conseil', color: 'indigo' },
  { id: 'reminder', label: 'Rappels', color: 'orange' },
];

// Sub-topic event colors (EventColorConfig) and the scheduler palette closest to each.
const SUB_TOPIC_COLORS: Record<string, EventCalendarColor> = {
  '#1e88e5': 'blue',
  '#3949ab': 'indigo',
  '#5e35b1': 'purple',
  '#8e24aa': 'purple',
  '#d81b60': 'pink',
  '#e53935': 'red',
  '#fb8c00': 'orange',
  '#ffa000': 'amber',
  '#43a047': 'green',
  '#00897b': 'teal',
  '#0097a7': 'teal',
  '#546e7a': 'grey',
};

function isCalendarItemType(type: string | null): type is CalendarItemType {
  return type === 'event' || type === 'council_meeting' || type === 'reminder';
}

export function toEventCalendarColor(color: string | null): EventCalendarColor | undefined {
  return color ? SUB_TOPIC_COLORS[color.toLowerCase()] : undefined;
}

export function toCalendarEvent(item: CalendarItemDatabase): EventCalendarEvent | null {
  if (!item.id || !item.date || !isCalendarItemType(item.type)) {
    return null;
  }

  const start = new Date(item.date);
  const allDay = Boolean(item.all_day) || isMidnight(start);
  const storedEnd = item.end_date ? new Date(item.end_date) : null;
  const end = storedEnd && storedEnd > start
    ? storedEnd
    : allDay ? start : addMinutes(start, DEFAULT_DURATION_MINUTES);

  return {
    id: item.id,
    title: item.title ?? 'Sans titre',
    start: start.toISOString(),
    end: end.toISOString(),
    allDay,
    categoryId: item.type,
    color: item.type === 'event' ? toEventCalendarColor(item.color) : undefined,
    variant: item.type === 'reminder' ? 'reminder' : 'default',
  };
}

export function toCalendarEvents(items: CalendarItemDatabase[]): EventCalendarEvent[] {
  return items.flatMap((item) => {
    const event = toCalendarEvent(item);
    return event ? [event] : [];
  });
}

/**
 * Fetch range of a view: the weeks of the visible month (which also cover the week and day views),
 * plus the next month for the agenda, which lists the days following the visible date.
 */
export function getCalendarRange(view: EventCalendarView, visibleDate: Date): CalendarRange {
  const lastMonth = view === 'agenda' ? addMonths(visibleDate, 1) : visibleDate;

  return {
    minDate: startOfWeek(startOfMonth(visibleDate), WEEK_OPTIONS).toISOString(),
    maxDate: endOfWeek(endOfMonth(lastMonth), WEEK_OPTIONS).toISOString(),
  };
}
